// Fiche d'un emplacement : zone, type, surface, état et motif, rubrique de redevance, attribution
// en cours ; tarification d'une période affichée avec la règle de calcul du module des recettes
// (GET /marches/emplacements/:id/tarification), sans rien émettre ; changement d'état motivé par le
// responsable des marchés ; historique des attributions.
import { CODES_PERIODICITES, ETATS_EMPLACEMENT_MANUELS } from '@cc/contrats';
import { Bandeau, Bouton, DateChamp, DialogueConfirmation, Lien, LienBouton, Panneau, PuceEtat, Selection, Tableau, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ErreurApi } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur, EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_MARCHES, modifierEmplacement, useEmplacement, useMarche, useTarification } from './api';
import { aujourdhuiIso, expliquerRefusAttribution, LIBELLES_ETAT_EMPLACEMENT, LIBELLES_PERIODICITE, LIBELLES_SITUATION, LIBELLES_SITUATION_EMPLACEMENT, montant, periodeParDefaut, situationEmplacement } from './logique';
import { libelleCommercant } from './logique';
import type { Attribution, EmplacementDetail, EtatEmplacement, PeriodiciteRedevance } from './types';
import styles from './marches.module.css';

/** Simulation d'une redevance : la règle vient du module des recettes, jamais d'un calcul local. */
export function Tarification({ emplacementId, periodicite }: { emplacementId: string; periodicite: PeriodiciteRedevance }) {
  const [periode, setPeriode] = useState(() => periodeParDefaut(periodicite));
  const [demandee, setDemandee] = useState<{ debut: string; fin: string } | null>(null);
  const t = useTarification(emplacementId, demandee);
  const invalide = periode.fin < periode.debut;
  return (
    <Panneau titre="Tarification d'une période" niveau={3} id="tarification">
      <form
        className={styles.barreFiltres}
        aria-label="Période à tarifer"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalide) setDemandee({ ...periode });
        }}
      >
        <DateChamp libelle="Du" value={periode.debut} onChange={(e) => setPeriode((p) => ({ ...p, debut: e.target.value }))} required />
        <DateChamp libelle="Au" value={periode.fin} onChange={(e) => setPeriode((p) => ({ ...p, fin: e.target.value }))} erreur={invalide ? 'La fin suit le début' : null} required />
        <Bouton type="submit" variante="secondaire" disabled={invalide}>
          Calculer la redevance
        </Bouton>
      </form>
      {demandee && t.isPending ? <p className={styles.aide}>Calcul par le module des recettes…</p> : null}
      {t.error instanceof ErreurApi && (t.error.statut === 409 || t.error.statut === 404 || t.error.statut === 400) ? (
        <Bandeau type="attention">{expliquerRefusAttribution(t.error.code, t.error.message, t.error.details)}</Bandeau>
      ) : t.error ? (
        <AffichageErreur erreur={t.error} onReessayer={() => void t.refetch()} fonction="La tarification" />
      ) : null}
      {t.data ? (
        <dl className={styles.definitions} aria-label="Redevance calculée">
          <dt>Période</dt>
          <dd>
            du {date(t.data.periode.debut)} au {date(t.data.periode.fin)}
          </dd>
          <dt>Montant</dt>
          <dd className={styles.montant} data-testid="montant-tarification">
            {montant(t.data.montant, t.data.fictif)}
          </dd>
          <dt>Règle</dt>
          <dd className={styles.regle}>{t.data.regle}</dd>
          <dt>Rubrique</dt>
          <dd className="cc-mono">{t.data.emplacement.rubrique_code}</dd>
        </dl>
      ) : null}
    </Panneau>
  );
}

function ChangementEtat({ e }: { e: EmplacementDetail }) {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState<EtatEmplacement>(e.etat === 'attribue' ? 'disponible' : e.etat);
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () => modifierEmplacement(e.id, { etat: etat as (typeof ETATS_EMPLACEMENT_MANUELS)[number], motif: motif.trim() || undefined }),
    succes: 'État de l\'emplacement enregistré.',
    invalider: INVALIDER_MARCHES,
    apres: () => setOuvert(false),
  });
  const motifRequis = etat === 'reserve' || etat === 'hors_service';
  return (
    <>
      <Bouton variante="secondaire" onClick={() => setOuvert(true)}>
        Changer l&apos;état
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`État de l'emplacement ${e.reference}`}
        message={<p>Réserver ou mettre hors service exige un motif ; un emplacement attribué ne peut pas changer d&apos;état tant que l&apos;attribution court.</p>}
        libelleConfirmer="Enregistrer"
        enCours={action.enCours}
        confirmationDesactivee={motifRequis && motif.trim().length < 3}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        <Selection libelle="Nouvel état" options={ETATS_EMPLACEMENT_MANUELS.map((v) => ({ valeur: v, libelle: LIBELLES_ETAT_EMPLACEMENT[v] }))} value={etat} onChange={(ev) => setEtat(ev.target.value as EtatEmplacement)} />
        <ZoneTexte libelle="Motif" value={motif} onChange={(ev) => setMotif(ev.target.value)} required={motifRequis} facultatif={!motifRequis} erreur={action.erreursChamps.find((c) => c.champ === 'motif')?.message} />
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

const TON_SITUATION: Record<string, 'ok' | 'attente' | 'retard' | 'neutre'> = { en_cours: 'ok', a_venir: 'attente', echue: 'neutre', resiliee: 'retard', terminee: 'neutre' };

export function PageEmplacement() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const emplacement = useEmplacement(id);
  const e = emplacement.data;
  const marche = useMarche(e?.marche_id ?? null);
  const aujourdhui = aujourdhuiIso();
  const situation = e ? situationEmplacement(e, aujourdhui) : null;
  const periodicite = (marche.data?.periodicite_defaut as PeriodiciteRedevance | undefined) ?? CODES_PERIODICITES[0];
  const colonnes: ColonneTableau<Attribution>[] = [
    { id: 'numero', entete: 'Attribution', enTeteLigne: true, masquable: false, cellule: (a) => <Lien href={`/marches/attributions/${a.id}`} className="cc-mono">{a.numero}</Lien>, valeurTri: (a) => a.numero },
    { id: 'commercant', entete: 'Commerçant', cellule: (a) => libelleCommercant(a) },
    { id: 'periode', entete: 'Période', cellule: (a) => `${date(a.periode.debut)} au ${date(a.periode.fin)}`, valeurTri: (a) => a.periode.debut },
    { id: 'periodicite', entete: 'Redevance', cellule: (a) => LIBELLES_PERIODICITE[a.periodicite as PeriodiciteRedevance] ?? a.periodicite },
    { id: 'situation', entete: 'Situation', cellule: (a) => <PuceEtat ton={TON_SITUATION[a.situation] ?? 'neutre'} libelle={LIBELLES_SITUATION[a.situation]} />, valeurTri: (a) => a.situation },
  ];
  return (
    <>
      <EnTetePage
        etiquette={e ? `${marche.data?.nom ?? 'Marché'} · ${e.zone.id ? `zone ${e.zone.code ?? '?'}` : 'sans zone'}${e.zone.nom ? ` (${e.zone.nom})` : ''}` : 'Emplacement'}
        titre={e ? `Emplacement ${e.reference}` : 'Emplacement'}
        description={e && situation ? <span>{e.type_libelle}, {e.surface_m2.toLocaleString('fr-FR')} m². Situation : {LIBELLES_SITUATION_EMPLACEMENT[situation]}{e.etat_motif ? ` (${e.etat_motif})` : ''}.</span> : null}
        actions={
          <>
            {e ? (
              <LienBouton href={`/marches/${e.marche_id}?emplacement=${e.id}`} icone={ArrowLeft}>
                Plan du marché
              </LienBouton>
            ) : null}
            {e && e.etat === 'disponible' && peut(contexte, 'marche_municipal', 'creer') ? (
              <LienBouton href={`/marches/attributions/nouvelle?emplacement=${e.id}`} variante="principal">
                Attribuer
              </LienBouton>
            ) : null}
            {e && peut(contexte, 'marche_municipal', 'modifier') ? <ChangementEtat e={e} /> : null}
            {e && marche.data?.mode === 'ciel_ouvert' && peut(contexte, 'marche_municipal', 'modifier') && peut(contexte, 'intervention', 'creer') ? <LienBouton href={`/terrain/places/${e.id}`} variante="secondaire">Ouvrir sur le terrain</LienBouton> : null}
          </>
        }
      />
      <EtatRequete chargement={emplacement.isPending} erreur={emplacement.error} onReessayer={() => void emplacement.refetch()} lot="L2-B" fonction="La fiche de l'emplacement">
        {e ? (
          <div className={styles.disposition}>
            <div className={styles.pile}>
              <Tarification emplacementId={e.id} periodicite={periodicite} />
              <Panneau titre="Attributions de l'emplacement" niveau={3}>
                {e.attributions.length === 0 ? <p className={styles.aide}>Aucune attribution enregistrée pour cet emplacement.</p> : <Tableau legende="Attributions de l'emplacement" colonnes={colonnes} lignes={e.attributions} cleLigne={(a) => a.id} cleColonnes="attributions-emplacement" />}
              </Panneau>
            </div>
            <aside className={styles.cote} aria-label="Situation">
              <Panneau titre="Situation" niveau={3}>
                <dl className={styles.definitions}>
                  <dt>État</dt>
                  <dd>{LIBELLES_ETAT_EMPLACEMENT[e.etat]}{e.etat_motif ? ` : ${e.etat_motif}` : ''}</dd>
                  <dt>Rubrique</dt>
                  <dd className="cc-mono">{e.rubrique_code}</dd>
                  {e.attribution_en_cours ? (
                    <>
                      <dt>Occupant</dt>
                      <dd>
                        {e.attribution_en_cours.commercant}, <Lien href={`/marches/attributions/${e.attribution_en_cours.id}`} className="cc-mono">{e.attribution_en_cours.numero}</Lien>
                        {e.attribution_en_cours.fin ? `, jusqu'au ${date(e.attribution_en_cours.fin)}` : ''}
                      </dd>
                    </>
                  ) : null}
                  {e.observations ? (
                    <>
                      <dt>Observations</dt>
                      <dd>{e.observations}</dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
            </aside>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
